import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Bookmark, Database, Menu, X, FolderOpen, Users, Send, BookOpen } from 'lucide-react';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import LoginButton from './LoginButton';
import ApiStatus from './ApiStatus';
import { useBookmarks } from '../contexts/BookmarkContext';
import { authService } from '../services/auth';
import { bugReportService } from '../services/bugReport';

// Helper function to check if dev mode is enabled via URL parameter
const checkDevMode = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('dev') === 'true';
  }
  return false;
};

const Header: React.FC = () => {
  const { bookmarks } = useBookmarks();
  const isDevMode = checkDevMode();
  const [isBugModalOpen, setIsBugModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const user = authService.getCurrentUser();
      setIsAuthenticated(!!user);
    };

    // Check immediately
    checkAuth();

    // Subscribe to auth changes
    const unsubscribe = authService.subscribe((user) => {
      setIsAuthenticated(!!user);
    });

    return unsubscribe;
  }, []);

  // Helper function to check if a route is active
  const isActiveRoute = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header className="nav-glass sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Navigation */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2 group">
                <Logo className="h-8 w-auto" />
                <span className="text-xl font-bold gradient-text">PatternLab Dashboard</span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-4">
              <Link
                to="/"
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActiveRoute('/')
                    ? 'bg-blue-500/20 dark:bg-blue-400/20 text-blue-700 dark:text-blue-300 shadow-lg shadow-blue-500/25'
                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-700/50'
                }`}
              >
                <Home className="w-4 h-4 inline mr-2" />
                Dashboard
              </Link>
              <Link
                to="/projects"
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActiveRoute('/projects')
                    ? 'bg-blue-500/20 dark:bg-blue-400/20 text-blue-700 dark:text-blue-300 shadow-lg shadow-blue-500/25'
                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-700/50'
                }`}
              >
                <FolderOpen className="w-4 h-4 inline mr-2" />
                Projects
              </Link>
              <Link
                to="/people"
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActiveRoute('/people')
                    ? 'bg-blue-500/20 dark:bg-blue-400/20 text-blue-700 dark:text-blue-300 shadow-lg shadow-blue-500/25'
                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-700/50'
                }`}
              >
                <Users className="w-4 h-4 inline mr-2" />
                People
              </Link>
              <Link
                to="/bookmarks"
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 relative ${
                  isActiveRoute('/bookmarks')
                    ? 'bg-blue-500/20 dark:bg-blue-400/20 text-blue-700 dark:text-blue-300 shadow-lg shadow-blue-500/25'
                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-700/50'
                }`}
              >
                <Bookmark className="w-4 h-4 inline mr-2" />
                Bookmarks
                {isAuthenticated && bookmarks.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {bookmarks.length > 99 ? '99+' : bookmarks.length}
                  </span>
                )}
              </Link>
              <Link
                to="/documentation"
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActiveRoute('/documentation')
                    ? 'bg-blue-500/20 dark:bg-blue-400/20 text-blue-700 dark:text-blue-300 shadow-lg shadow-blue-500/25'
                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-700/50'
                }`}
              >
                <BookOpen className="w-4 h-4 inline mr-2" />
                Documentation
              </Link>
              {isDevMode && (
                <Link
                  to="/api-test"
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                    isActiveRoute('/api-test')
                      ? 'bg-blue-500/20 dark:bg-blue-400/20 text-blue-700 dark:text-blue-300 shadow-lg shadow-blue-500/25'
                      : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-700/50'
                  }`}
                >
                  <Database className="w-4 h-4 inline mr-2" />
                  API Test
                </Link>
              )}
            </nav>

            {/* Meta Navigation - Theme, User, and Sign Out */}
            <div className="hidden md:flex items-center space-x-3">
              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600"></div>
              <ThemeToggle />
              <LoginButton />
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white p-2 rounded-md"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 dark:border-gray-700 py-4">
              <nav className="space-y-2">
                <Link
                  to="/"
                  className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Home className="w-4 h-4 mr-2" />
                  Dashboard
                </Link>
                <Link
                  to="/projects"
                  className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Projects
                </Link>
                <Link
                  to="/people"
                  className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Users className="w-4 h-4 mr-2" />
                  People
                </Link>
                <Link
                  to="/bookmarks"
                  className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors relative"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Bookmark className="w-4 h-4 mr-2" />
                  Bookmarks
                  {isAuthenticated && bookmarks.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {bookmarks.length > 99 ? '99+' : bookmarks.length}
                    </span>
                  )}
                </Link>
                <Link
                  to="/documentation"
                  className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Documentation
                </Link>
                {isDevMode && (
                  <Link
                    to="/api-test"
                    className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Database className="w-4 h-4 mr-2" />
                    API Test
                  </Link>
                )}
                
                {/* Mobile Meta Navigation Separator */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Settings
                    </span>
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between px-3 py-2">
                      <span className="text-sm text-gray-700 dark:text-gray-300">Theme</span>
                      <ThemeToggle />
                    </div>
                    <div className="px-3 py-2">
                      <LoginButton />
                    </div>
                  </div>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>
      
      {/* Development Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-700">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  🚧 This app is in development
                </span>
              </div>
              <span className="text-sm text-blue-600 dark:text-blue-300">
                Feedback welcome via{' '}
                <a 
                  href="https://logic-joe.slack.com/team/p.amelang"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 underline font-medium"
                >
                  Slack to Peter Amelang
                </a>
                {' '}or email to{' '}
                <a 
                  href="mailto:p.amelang@logic-joe.com?subject=PatternLab%20Dashboard%20Feedback&body=Hi%20Peter%2C%0A%0AI%20have%20feedback%20about%20the%20PatternLab%20Dashboard%3A%0A%0A"
                  className="text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 underline font-medium"
                >
                  p.amelang@logic-joe.com
                </a>
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-blue-500 dark:text-blue-400">or use the</span>
              <button 
                onClick={() => setIsBugModalOpen(true)}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 dark:text-blue-200 dark:bg-blue-800 dark:hover:bg-blue-700 rounded transition-colors"
              >
                🐛 Report Bug
              </button>
              <span className="text-xs text-blue-500 dark:text-blue-400">button</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Dev Info Bar - Only visible when dev mode is enabled */}
      {isDevMode && <DevInfoBar />}
      <BugReportModal isOpen={isBugModalOpen} onClose={() => setIsBugModalOpen(false)} />
    </>
  );
};

// Dev Info Bar Component
const DevInfoBar: React.FC = () => {
  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-700">
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Development Mode Active
              </span>
            </div>
            <span className="text-xs text-yellow-600 dark:text-yellow-300">
              API debugging and status information enabled
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <ApiStatus />
          </div>
        </div>
      </div>
    </div>
  );
};

// Bug Report Modal Component
const BugReportModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const result = await bugReportService.submitBugReport(message);
      
      if (result.success) {
        setSubmitStatus('success');
        setMessage('');
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Failed to submit bug report:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Report a Bug
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Describe the issue:
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              rows={4}
              placeholder="Please describe the bug you encountered..."
              required
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {authService.getCurrentUser() ? (
                `Reporting as: ${authService.getCurrentUser()?.name}`
              ) : (
                'Reporting anonymously'
              )}
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !message.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Send Report</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="mt-4 p-3 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-md">
              Bug report sent successfully!
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-4 p-3 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-md">
              Failed to send report. Please try again.
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Header;
